import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { PageHeader, Table, Badge, Modal, Field, Input, Select, Btn, Card, useDirtyGuard, ConfirmClose, Pager, SearchBox, usePaged } from "@/components/erp/ui";
import { ConfirmBar, StageSla, SlaReportModal, useSlaTol } from "@/components/erp/WoSla";
import { DesignErrors } from "@/components/erp/DesignErrors";
import { CustomerReject } from "@/components/erp/CustomerReject";
import { WoRefs, ApprovalHistory, RevisionHistory, useWoHistory } from "@/components/erp/WoHistory";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash2 } from "lucide-react";

const EMPTY = { product: "", project_id: "", customer_id: "", quantity: 1, material: "", priority: "MEDIUM", selling_price: 0, deadline: "", workflow_id: "", bom: [], notes: "" };
const STAGE_STATUS = ["WAITING","IN_PROGRESS","COMPLETED","REWORK","SKIPPED"];
const STATUS_DOT = { WAITING: "bg-slate-300", IN_PROGRESS: "bg-sky-500", COMPLETED: "bg-emerald-500", REWORK: "bg-rose-500", SKIPPED: "bg-slate-200" };

const PRIO_CLS = { URGENT: "bg-red-100 text-red-700 border-red-200", HIGH: "bg-orange-100 text-orange-700 border-orange-200", MEDIUM: "bg-amber-50 text-amber-700 border-amber-200", LOW: "bg-slate-50 text-slate-500 border-slate-200" };
const WoNotesEdit = ({ wo, onSaved }) => {
  const [v, setV] = useState(wo.notes || "");
  useEffect(() => { setV(wo.notes || ""); }, [wo.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => { try { await api.put(`/workorders/${wo.id}/notes`, { notes: v }); toast.success("Catatan customer disimpan"); onSaved(v); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } };
  return (
    <div className="mb-4" data-testid="wo-notes-box">
      <div className="text-xs text-slate-500 mb-1">Catatan customer (tampil di Dashboard Produksi, Retouch & semua proses)</div>
      <textarea rows={2} data-testid="wo-notes-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={v} onChange={(e) => setV(e.target.value)} />
      {v !== (wo.notes || "") && <Btn className="mt-1" data-testid="wo-notes-save" onClick={save}>Simpan catatan</Btn>}
    </div>
  );
};

const deadlineInfo = (d, progress) => {
  if (!d || progress >= 100) return { cls: "bg-slate-100 text-slate-600", label: "" };
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const days = Math.round((new Date(d.slice(0, 10)) - new Date(today)) / 86400000);
  if (days < 0) return { cls: "bg-red-600 text-white font-semibold", label: `lewat ${-days} hari` };
  if (days <= 2) return { cls: "bg-red-100 text-red-700 font-semibold", label: days === 0 ? "hari ini" : `${days} hari lagi` };
  return { cls: "bg-slate-100 text-slate-600", label: `${days} hari lagi` };
};

export default function WorkOrders() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stock, setStock] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const g = useDirtyGuard();
  const gConv = useDirtyGuard();
  const forceClose = () => { g.clear(); setOpen(false); setForm(EMPTY); };
  const tryClose = () => g.attemptClose(form, forceClose);
  const forceCloseConv = () => { gConv.clear(); setConvert(null); };
  const tryCloseConv = () => gConv.attemptClose(convForm, forceCloseConv);
  const [sel, setSel] = useState(null);
  const hist = useWoHistory(sel?.id);
  const [presets, setPresets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [prs, setPrs] = useState([]);
  const [convert, setConvert] = useState(null);
  const [convForm, setConvForm] = useState({ supplier_id: "", unit_price: 0 });

  const load = () => api.get("/workorders").then(({ data }) => setRows(data)).catch(() => {});
  const [slaOpen, setSlaOpen] = useState(false);
  const [slaTol] = useSlaTol();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const openParam = sp.get("open");
  useEffect(() => {
    if (!openParam || !rows.length) return;
    const w = rows.find((x) => x.id === openParam);
    if (w) setSel(w);
    setSp({}, { replace: true });
  }, [openParam, rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadPrs = () => api.get("/purchase-requests").then(({ data }) => setPrs(data)).catch(() => {});
  useEffect(() => { load(); loadPrs();
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/stock").then(({ data }) => setStock(data)).catch(() => {});
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/workflows").then(({ data }) => setWorkflows(data)).catch(() => {});
    api.get("/bom-presets").then(({ data }) => setPresets(data)).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.product) { toast.error("Produk wajib"); return; }
    const bom = (form.bom || []).filter((b) => b.stock_id && +b.qty > 0).map((b) => ({ stock_id: b.stock_id, qty: +b.qty }));
    const { data } = await api.post("/workorders", { ...form, quantity: +form.quantity, selling_price: +form.selling_price, bom });
    if (data.backorder_prs?.length) toast.warning(`Work Order dibuat. Stok kurang → Purchase Request: ${data.backorder_prs.join(", ")}`);
    else toast.success("Work Order dibuat" + (bom.length ? " — stok ter-reserve otomatis" : ""));
    setOpen(false); setForm(EMPTY); load();
  };
  const addBom = () => setForm((f) => ({ ...f, bom: [...(f.bom || []), { stock_id: "", qty: 1 }] }));
  const setBom = (i, k, v) => setForm((f) => { const b = [...(f.bom || [])]; b[i] = { ...b[i], [k]: v }; return { ...f, bom: b }; });
  const rmBom = (i) => setForm((f) => ({ ...f, bom: (f.bom || []).filter((_, j) => j !== i) }));
  const savePreset = async () => {
    const items = (form.bom || []).filter((b) => b.stock_id && +b.qty > 0).map((b) => ({ stock_id: b.stock_id, qty: +b.qty }));
    if (!form.product) { toast.error("Isi nama produk dulu"); return; }
    if (!items.length) { toast.error("Tambah minimal 1 bahan"); return; }
    await api.post("/bom-presets", { product: form.product, items });
    toast.success(`BOM preset "${form.product}" tersimpan`);
    api.get("/bom-presets").then(({ data }) => setPresets(data)).catch(() => {});
  };
  const loadPreset = (id) => {
    const p = presets.find((x) => x.id === id); if (!p) return;
    setForm((f) => ({ ...f, product: f.product || p.product, bom: (p.items || []).map((i) => ({ stock_id: i.stock_id, qty: i.qty })) }));
    toast.success(`BOM "${p.product}" dimuat`);
  };
  const openConvert = (pr) => { const f = { supplier_id: suppliers[0]?.id || "", unit_price: 0 }; setConvert(pr); setConvForm(f); gConv.snapshot(f); };
  const doConvert = async () => {
    if (!convForm.supplier_id) { toast.error("Pilih supplier"); return; }
    if (+convForm.unit_price <= 0) { toast.error("Harga harus > 0"); return; }
    await api.post(`/purchase-requests/${convert.id}/convert`, { supplier_id: convForm.supplier_id, unit_price: +convForm.unit_price });
    toast.success("PR dikonversi menjadi Purchase Order"); setConvert(null); loadPrs();
  };
  const backorders = prs.filter((p) => p.status === "OPEN" && p.wo_id);
  const [boQ, setBoQ] = useState("");
  const boS = boQ.trim().toLowerCase();
  const boFiltered = boS ? backorders.filter((p) => [p.wo_code, p.stock_name, p.code].some((v) => String(v || "").toLowerCase().includes(boS))) : backorders;
  const boPaged = usePaged(boFiltered, [5, 15]);
  const PRIO_RANK = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  const sortedRows = [...rows].sort((a, b) => ((PRIO_RANK[a.priority] ?? 2) - (PRIO_RANK[b.priority] ?? 2)) || String(a.deadline || "9999").localeCompare(String(b.deadline || "9999")) || ((b.express ? 1 : 0) - (a.express ? 1 : 0)));
  const setPriority = async (w, priority) => {
    try { await api.put(`/workorders/${w.id}/priority`, { priority }); toast.success(`${w.code} → ${priority}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah priority"); }
  };

  const [reservations, setReservations] = useState([]);
  const [resForm, setResForm] = useState({ stock_id: "", qty: 0 });
  useEffect(() => {
    if (sel) api.get(`/workorders/${sel.id}/reservations`).then(({ data }) => setReservations(data)).catch(() => {});
  }, [sel]);

  const setStage = async (stageKey, status, pic_id, pic, branch, actual) => {
    const cur = (sel.stages || []).find((x) => (x.id || x.name) === stageKey) || {};
    const { data } = await api.put(`/workorders/${sel.id}/stage`, { stage: stageKey, status: status ?? cur.status, pic_id: pic_id ?? cur.pic_id, pic: pic ?? cur.pic, branch, ...(actual || {}) });
    setSel(data); load();
  };
  const [asgForm, setAsgForm] = useState({});
  const setAsg = (k, f, v) => setAsgForm((s) => ({ ...s, [k]: { people: 1, percent: 100, ...(s[k] || {}), [f]: v } }));
  const saveAssignees = async (s, assignees) => {
    const { data } = await api.put(`/workorders/${sel.id}/stage`, { stage: s.id || s.name, status: s.status, assignees });
    setSel(data); load();
    toast.success("Tim design diperbarui");
  };
  const addAssignee = (s) => {
    const k = s.id || s.name; const f = asgForm[k] || {};
    if (!f.employee_id) { toast.error("Pilih anggota tim"); return; }
    const em = employees.find((x) => x.id === f.employee_id); if (!em) return;
    saveAssignees(s, [...(s.assignees || []), { employee_id: em.id, name: em.name, people: Math.max(1, Math.min(3, +f.people || 1)), percent: Math.max(1, Math.min(100, +f.percent || 100)) }]);
    setAsgForm((st) => ({ ...st, [k]: { people: 1, percent: 100 } }));
  };
  const reserve = async () => {
    if (!resForm.stock_id || !resForm.qty) { toast.error("Pilih material & qty"); return; }
    const { data } = await api.post(`/workorders/${sel.id}/reserve`, { stock_id: resForm.stock_id, qty: +resForm.qty });
    if (data.purchase_request) toast.warning(`Stok kurang — Purchase Request ${data.purchase_request.code} dibuat`);
    else toast.success("Material ter-reserve");
    setResForm({ stock_id: "", qty: 0 });
    api.get(`/workorders/${sel.id}/reservations`).then(({ data }) => setReservations(data)).catch(() => {});
  };

  return (
    <div>
      <PageHeader title="Work Orders" subtitle="Root data — setiap WO membawa 9 tahap produksi sebagai node." action={<div className="flex gap-2"><Btn variant="ghost" data-testid="sla-report-btn" onClick={() => setSlaOpen(true)}>Waktu Tunggu</Btn><Btn data-testid="add-wo-btn" onClick={() => { setForm(EMPTY); g.snapshot(EMPTY); setOpen(true); }}><Plus className="w-4 h-4" /> Work Order</Btn></div>} />
      <SlaReportModal open={slaOpen} onClose={() => setSlaOpen(false)} owner={user?.role === "owner"} />
      {backorders.length > 0 && (
        <Card className="p-4 mb-4 border-amber-200 bg-amber-50/60" data-testid="backorder-panel">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-xs font-mono text-amber-700">⚠ BACKORDER — stok kurang, perlu di-PO ({backorders.length})</div>
            <SearchBox value={boQ} onChange={setBoQ} testid="backorder-search" placeholder="Cari WO, bahan, kode PR…" />
          </div>
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1" data-testid="backorder-list">
            {boPaged.slice.length === 0 && <p className="text-xs text-slate-400 py-2">Tidak ada hasil pencarian</p>}
            {boPaged.slice.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-sm bg-white border border-amber-200 rounded-lg px-3 py-2" data-testid={`backorder-${p.id}`}>
                <span className="truncate min-w-0">{p.wo_code ? <b>{p.wo_code}</b> : null} · {p.stock_name} · {p.qty} {p.unit} <span className="text-[11px] text-slate-400 font-mono">PR {p.code}</span></span>
                <Btn variant="accent" data-testid={`convert-pr-${p.id}`} onClick={() => openConvert(p)}>Konversi ke PO</Btn>
              </div>
            ))}
          </div>
          <div className="mt-2 -mx-4 -mb-4"><Pager p={boPaged} testid="backorder-pager" unit="backorder" /></div>
        </Card>
      )}
      <Table testid="wo-table" columns={["Kode","Customer","Qty Total","Priority","Deadline","Progress","Status / Tahap",""]} rows={sortedRows} renderRow={(w) => {
        const dl = deadlineInfo(w.deadline, w.progress);
        return (
        <tr key={w.id} className={`border-b border-slate-100 last:border-0 ${w.express ? "bg-yellow-100/80 font-bold hover:bg-yellow-100" : "hover:bg-slate-50"}`} data-testid={`wo-row-${w.id}`}>
          <td className="px-4 py-3 font-mono text-xs">{w.code}{w.express && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold" data-testid={`wo-express-${w.id}`}>EXPRESS</span>}</td>
          <td className="px-4 py-3">{w.customer_name || "-"}</td>
          <td className="px-4 py-3" data-testid={`wo-qty-${w.id}`}>{w.quantity}</td>
          <td className="px-4 py-3">
            <select data-testid={`wo-priority-${w.id}`} disabled={user?.role === "viewer"} value={w.priority || "MEDIUM"} onChange={(e) => setPriority(w, e.target.value)} className={`text-xs font-semibold rounded-md border px-1.5 py-1 ${PRIO_CLS[w.priority] || PRIO_CLS.MEDIUM}`}>
              {["URGENT", "HIGH", "MEDIUM", "LOW"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </td>
          <td className="px-4 py-3 whitespace-nowrap" data-testid={`wo-deadline-${w.id}`}>{w.deadline ? <span className={`px-2 py-0.5 rounded text-xs ${dl.cls}`}>{w.deadline.slice(0, 10)}{dl.label && ` · ${dl.label}`}</span> : <span className="text-slate-300 text-xs">—</span>}</td>
          <td className="px-4 py-3 w-40">
            <div className="flex items-center gap-2"><div className="flex-1 bg-slate-100 rounded-full h-2"><div className="bg-sky-500 h-2 rounded-full" style={{ width: `${w.progress}%` }} /></div><span className="text-xs">{w.progress}%</span></div>
          </td>
          <td className="px-4 py-3" data-testid={`wo-stage-${w.id}`}><Badge>{w.current_stage || w.status}</Badge></td>
          <td className="px-4 py-3 text-right"><button data-testid={`open-wo-${w.id}`} onClick={() => setSel(w)} className="text-sky-600 text-sm hover:underline">Buka</button></td>
        </tr>
        );
      }} />

      {/* Create modal */}
      <Modal open={open} onClose={tryClose} title="Work Order Baru"
        footer={<><Btn variant="ghost" onClick={tryClose}>Batal</Btn><Btn data-testid="save-wo-btn" onClick={save}>Simpan</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Produk *"><Input data-testid="wf-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} /></Field></div>
          <Field label="Project"><Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
          <Field label="Customer"><Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}><option value="">—</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Quantity"><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
          <Field label="Material"><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} /></Field>
          <Field label="Priority"><Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{["LOW","MEDIUM","HIGH"].map(p => <option key={p}>{p}</option>)}</Select></Field>
          <Field label="Selling Price"><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Catatan customer (muncul di semua proses produksi)"><textarea rows={2} data-testid="wf-notes" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Workflow (opsional)"><Select data-testid="wf-select" value={form.workflow_id} onChange={(e) => setForm({ ...form, workflow_id: e.target.value })}><option value="">Default 9 tahap produksi</option>{workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</Select></Field></div>
          <div className="col-span-2 border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <span className="text-xs font-mono text-slate-500">BILL OF MATERIALS — stok ter-reserve otomatis saat WO dibuat</span>
              <div className="flex items-center gap-2 flex-wrap">
                {presets.length > 0 && (
                  <Select data-testid="wo-preset-select" value="" onChange={(e) => loadPreset(e.target.value)} className="h-9 text-xs">
                    <option value="">Muat preset…</option>
                    {presets.map((p) => <option key={p.id} value={p.id}>{p.product} ({(p.items || []).length} bahan)</option>)}
                  </Select>
                )}
                <Btn variant="ghost" data-testid="wo-save-preset" onClick={savePreset}>Simpan Preset</Btn>
                <Btn variant="ghost" data-testid="wo-add-bom" onClick={addBom}><Plus className="w-4 h-4" /> Bahan</Btn>
              </div>
            </div>
            {(form.bom || []).length === 0 && <p className="text-[11px] text-slate-400 mb-1">Opsional. Tambah bahan + qty per unit; sistem otomatis reserve stok (atau buat Purchase Request bila kurang).</p>}
            <div className="space-y-2">
              {(form.bom || []).map((b, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`wo-bom-${i}`}>
                  <Select data-testid={`wo-bom-stock-${i}`} value={b.stock_id} onChange={(e) => setBom(i, "stock_id", e.target.value)}>
                    <option value="">Pilih bahan…</option>
                    {stock.map((s) => <option key={s.id} value={s.id}>{s.name} (avail {(s.current_stock || 0) - (s.reserved || 0)} {s.unit})</option>)}
                  </Select>
                  <Input type="number" data-testid={`wo-bom-qty-${i}`} className="w-28" placeholder="Qty/unit" value={b.qty} onChange={(e) => setBom(i, "qty", e.target.value)} />
                  <button onClick={() => rmBom(i)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {(form.bom || []).length > 0 && +form.quantity > 1 && <p className="text-[11px] text-slate-400 mt-1">Total reserve = qty/unit × {form.quantity} unit.</p>}
          </div>
        </div>
      </Modal>

      {/* Detail / node flow */}
      <Modal open={!!sel} onClose={() => setSel(null)} title={`${sel?.code} · ${sel?.product}`}>
        {sel && (
          <div>
            <div className="flex gap-4 text-sm mb-5">
              <div><div className="text-xs text-slate-500">Qty</div>{sel.quantity}</div>
              <div><div className="text-xs text-slate-500">Priority</div><Badge>{sel.priority}</Badge></div>
              <div><div className="text-xs text-slate-500">Harga</div>{formatIDR(sel.selling_price)}</div>
            </div>
            <WoNotesEdit wo={sel} onSaved={(n) => { setSel({ ...sel, notes: n }); load(); }} />
            <CustomerReject wo={sel} done={() => { setSel(null); load(); }} />
            <WoRefs h={hist} />
            <ConfirmBar wo={sel} onDone={(data) => { setSel(data); load(); }} />
            <DesignErrors wo={sel} />
            <ApprovalHistory h={hist} />
            <RevisionHistory woId={sel.id} />
            <div className="text-xs font-mono text-slate-500 mb-3">DATA TREE · PRODUCTION WORKFLOW</div>
            <div className="space-y-2">
              {sel.stages?.map((s, i) => (
                <div key={s.name}>
                  <Card className="p-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[s.status] || "bg-slate-300"}`} />
                      <span className="font-medium text-sm w-24">{s.name}{s.type === "DECISION" && <span className="ml-1 text-[10px] text-amber-600 font-mono">◆</span>}</span>
                      <Select value={s.status} onChange={(e) => setStage(s.id || s.name, e.target.value)}>
                        {STAGE_STATUS.map((st) => <option key={st}>{st}</option>)}
                      </Select>
                      <Select data-testid={`stage-pic-${s.id || s.name}`} value={s.pic_id || ""} onChange={(e) => { const em = employees.find((x) => x.id === e.target.value); setStage(s.id || s.name, undefined, e.target.value, em ? em.name : ""); }}>
                        <option value="">PIC —</option>
                        {employees.map((em) => <option key={em.id} value={em.id}>{em.name}</option>)}
                        {s.pic_id && !employees.some((em) => em.id === s.pic_id) && <option value={s.pic_id}>{s.pic || s.pic_id} · member</option>}
                      </Select>
                    </div>
                    <StageSla wo={sel} s={s} tol={slaTol} onDone={(data) => { setSel(data); load(); }} />
                    <div className="flex items-center gap-2 mt-1.5 pl-5 text-[11px] text-slate-500 flex-wrap" data-testid={`stage-pic-info-${s.id || s.name}`}>
                      <span>Plan: <b className="text-slate-700">{s.pic || "—"}</b></span>
                      {s.pic_source && <span className={`px-1.5 rounded font-mono ${s.pic_source === "auto" ? "bg-sky-50 text-sky-600" : "bg-slate-100"}`} data-testid={`stage-pic-source-${s.id || s.name}`}>{s.pic_source === "auto" ? "AUTO" : "MANUAL"}</span>}
                      <span className="ml-auto">Dikerjakan oleh (aktual):</span>
                      <select data-testid={`stage-actual-pic-${s.id || s.name}`} value={s.actual_pic_id || ""} onChange={(e) => { const em = employees.find((x) => x.id === e.target.value); setStage(s.id || s.name, undefined, undefined, undefined, undefined, { actual_pic_id: e.target.value, actual_pic: em ? em.name : "" }); }} className={`border rounded px-2 h-8 text-xs ${s.actual_pic_id && s.actual_pic_id !== s.pic_id ? "border-amber-400 bg-amber-50 text-amber-700" : "border-slate-200"}`}>
                        <option value="">ikut plan</option>
                        {employees.map((em) => <option key={em.id} value={em.id}>{em.name}</option>)}
                      </select>
                    </div>
                    {s.type === "DECISION" && (sel.edges || []).some((e) => e.source === s.id) && (
                      <div className="flex flex-wrap gap-2 mt-2 pl-5">
                        {(sel.edges || []).filter((e) => e.source === s.id).map((e, bi) => (
                          <button key={bi} data-testid={`branch-${s.id}-${bi}`} onClick={() => setStage(s.id, "COMPLETED", s.pic_id, s.pic, e.label)} className="text-xs px-2.5 py-1 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium">
                            {e.label || "lanjut"} →
                          </button>
                        ))}
                      </div>
                    )}
                    {/design/i.test(s.name) && (
                      <div className="mt-2 pl-5 border-l-2 border-sky-100 space-y-1.5" data-testid={`stage-assignees-${s.id || s.name}`}>
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Tim Design (1 anggota bisa dihitung &gt;1 orang)</div>
                        {s.people_required ? (() => { const alloc = (s.assignees || []).reduce((t, a) => t + (a.people || 1), 0); const ok = alloc >= s.people_required; return (
                          <div className={`text-[11px] font-medium ${ok ? "text-emerald-600" : "text-amber-600"}`} data-testid={`stage-alloc-${s.id || s.name}`}>
                            Kebutuhan dari quotation: {s.people_required} orang · teralokasi {alloc} orang {ok ? "✓ sesuai" : "— kurang, tambah anggota/orang"}
                          </div>
                        ); })() : null}
                        {(s.assignees || []).map((a, ai) => (
                          <div key={ai} className="flex items-center gap-2 text-xs" data-testid={`stage-assignee-${s.id || s.name}-${ai}`}>
                            <span className="font-medium">{a.name}</span>
                            <span className="text-slate-400">×{a.people} orang · {a.percent}% porsi</span>
                            <button data-testid={`stage-assignee-del-${s.id || s.name}-${ai}`} onClick={() => saveAssignees(s, (s.assignees || []).filter((_, j) => j !== ai))} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Select data-testid={`stage-assignee-emp-${s.id || s.name}`} value={(asgForm[s.id || s.name] || {}).employee_id || ""} onChange={(e) => setAsg(s.id || s.name, "employee_id", e.target.value)}>
                            <option value="">Anggota…</option>
                            {employees.map((em) => <option key={em.id} value={em.id}>{em.name}</option>)}
                          </Select>
                          <Select data-testid={`stage-assignee-people-${s.id || s.name}`} value={(asgForm[s.id || s.name] || {}).people || 1} onChange={(e) => setAsg(s.id || s.name, "people", e.target.value)}>
                            {[1, 2, 3].map((n) => <option key={n} value={n}>{n} orang</option>)}
                          </Select>
                          <Input type="number" min="1" max="100" className="w-20" data-testid={`stage-assignee-pct-${s.id || s.name}`} value={(asgForm[s.id || s.name] || {}).percent ?? 100} onChange={(e) => setAsg(s.id || s.name, "percent", e.target.value)} />
                          <span className="text-[10px] text-slate-400">%</span>
                          <button data-testid={`stage-assignee-add-${s.id || s.name}`} onClick={() => addAssignee(s)} className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah</button>
                        </div>
                      </div>
                    )}
                  </Card>
                  {i < sel.stages.length - 1 && <div className="flex justify-center py-0.5"><ArrowRight className="w-3.5 h-3.5 text-slate-300 rotate-90" /></div>}
                </div>
              ))}
            </div>
            <div className="mt-6">
              <div className="text-xs font-mono text-slate-500 mb-3">MATERIAL RESERVATION</div>
              <div className="flex gap-2 mb-3">
                <Select data-testid="reserve-stock-select" value={resForm.stock_id} onChange={(e) => setResForm({ ...resForm, stock_id: e.target.value })}>
                  <option value="">Pilih material…</option>
                  {stock.map((s) => <option key={s.id} value={s.id}>{s.name} (avail {(s.current_stock || 0) - (s.reserved || 0)} {s.unit})</option>)}
                </Select>
                <Input type="number" data-testid="reserve-qty" placeholder="Qty" value={resForm.qty} onChange={(e) => setResForm({ ...resForm, qty: e.target.value })} className="w-24" />
                <Btn data-testid="reserve-btn" onClick={reserve}>Reserve</Btn>
              </div>
              {reservations.length > 0 && (
                <div className="space-y-1.5">
                  {reservations.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg px-3 py-2">
                      <span>{r.stock_name} · {r.qty} {r.unit}{r.pr_code ? ` · PR ${r.pr_code}` : ""}</span>
                      <Badge>{r.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Convert PR -> PO */}
      <Modal open={!!convert} onClose={tryCloseConv} title={`Konversi PR → PO · ${convert?.stock_name || ""}`}
        footer={<><Btn variant="ghost" onClick={tryCloseConv}>Batal</Btn><Btn data-testid="confirm-convert-btn" onClick={doConvert}>Buat PO</Btn></>}>
        {convert && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{convert.stock_name} · {convert.qty} {convert.unit}{convert.wo_code ? ` (dari ${convert.wo_code})` : ""}</p>
            <Field label="Supplier">
              <Select data-testid="conv-supplier" value={convForm.supplier_id} onChange={(e) => setConvForm({ ...convForm, supplier_id: e.target.value })}>
                <option value="">Pilih supplier…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field label="Harga satuan (Rp)"><Input type="number" data-testid="conv-price" value={convForm.unit_price} onChange={(e) => setConvForm({ ...convForm, unit_price: e.target.value })} /></Field>
            {suppliers.length === 0 && <p className="text-[11px] text-rose-500">Belum ada supplier. Tambahkan dulu di menu Purchasing.</p>}
          </div>
        )}
      </Modal>
      <ConfirmClose open={g.confirmOpen} testid="wo-confirm-close" onCancel={() => g.setConfirmOpen(false)} onDiscard={forceClose} onSave={async () => { g.setConfirmOpen(false); await save(); }} />
      <ConfirmClose open={gConv.confirmOpen} testid="wo-conv-confirm-close" onCancel={() => gConv.setConfirmOpen(false)} onDiscard={forceCloseConv} onSave={async () => { gConv.setConfirmOpen(false); await doConvert(); }} />
    </div>
  );
}
